import { useEffect, useMemo, useRef, useState } from "react";
import { useBusinessUnitStore } from "@/stores/businessUnit";
import Autocomplete from "@/components/common/GoogleAutocomplete";
import TextField from "@/components/common/GoogleTextField";
import IconButton from "@/components/common/GoogleIconButton";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import { Building2 } from "lucide-react";
import { useConfirmDiscard } from "@/hooks/useUnsavedChanges";

export function BusinessUnitSelector() {
  const requestDiscard = useConfirmDiscard();
  const currentBusinessUnit = useBusinessUnitStore(
    (s) => s.currentBusinessUnit,
  );
  const businessUnits = useBusinessUnitStore((s) => s.businessUnits);
  const setCurrentBusinessUnit = useBusinessUnitStore(
    (s) => s.setCurrentBusinessUnit,
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const currentName = businessUnits.find(
    (unit) => String(unit.id) === currentBusinessUnit,
  )?.name;
  const selectBusinessUnit = (value: string) => {
    if (value === currentBusinessUnit) return;
    requestDiscard(() => setCurrentBusinessUnit(value));
  };
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const options = useMemo(
    () =>
      [...businessUnits]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((unit) => ({ value: unit.id, label: unit.name })),
    [businessUnits],
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [options, search]);

  useEffect(() => {
    if (businessUnits.length > 0 && !currentBusinessUnit) {
      setCurrentBusinessUnit(String(businessUnits[0].id));
    }
  }, [businessUnits, currentBusinessUnit, setCurrentBusinessUnit]);

  useEffect(() => {
    if (menuAnchor) {
      const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [menuAnchor]);

  if (businessUnits.length === 0) {
    return null;
  }

  if (businessUnits.length === 1) {
    return (
      <span className="text-sm font-medium text-muted-foreground px-2 hidden sm:inline">
        {businessUnits[0].name}
      </span>
    );
  }

  const handleCloseMobileMenu = () => {
    setMenuAnchor(null);
    setSearch("");
  };

  return (
    <>
      {/* Mobile: compact business unit menu */}
      <IconButton
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        className="sm:hidden"
        aria-label={
          currentName
            ? `Select business unit, current facility: ${currentName}`
            : "Select business unit"
        }
        sx={{
          width: "2.25rem",
          height: "2.25rem",
          borderRadius: "0.75rem",
          color: "var(--muted-foreground)",
          "&:hover": {
            backgroundColor: "var(--foreground-4)",
            color: "var(--foreground)",
          },
        }}
      >
        <Building2 className="h-[15px] w-[15px]" />
      </IconButton>
      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleCloseMobileMenu}
        disableAutoFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { width: 260, marginTop: "0.5rem" } },
        }}
      >
        <Box
          sx={{ p: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              handleCloseMobileMenu();
            }
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Search business unit"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputRef={searchInputRef}
          />
        </Box>
        <MenuList dense sx={{ maxHeight: 320, overflow: "auto" }}>
          {filteredOptions.length === 0 && (
            <MenuItem disabled sx={{ fontSize: "0.875rem" }}>
              No business units found
            </MenuItem>
          )}
          {filteredOptions.map((o) => (
            <MenuItem
              key={o.value}
              selected={o.value === currentBusinessUnit}
              onClick={() => {
                selectBusinessUnit(String(o.value));
                setMenuAnchor(null);
                setSearch("");
              }}
              sx={{ borderRadius: "0.5rem" }}
            >
              {o.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>

      {/* Desktop: autocomplete */}
      <Autocomplete
        options={options}
        value={
          options.find((o) => o.value === currentBusinessUnit) ?? undefined
        }
        onChange={(_, option) =>
          selectBusinessUnit(option ? String(option.value) : "")
        }
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, s) => o.value === s.value}
        disableClearable
        autoHighlight
        openOnFocus
        className="hidden sm:flex w-[140px] sm:w-[200px] shrink-0"
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Select Business Unit"
          />
        )}
      />
    </>
  );
}
